import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import {
  useEnrollmentChecklistsQuery,
  type EnrollmentChecklistStep,
  type EnrollmentChecklistVersion,
} from './useEnrollmentChecklistsQuery';
import { SettingsLoading } from './SettingsPanel';

interface EnrollmentChecklistsProps {
  schoolId: string;
  schoolName: string;
}

const KIND_HEADINGS: Record<string, string> = {
  RE_ENROLLMENT: 'Re-enrollment',
  ADMISSION: 'Admission',
};

const yesNo = (value: boolean) => (value ? 'Yes' : 'No');

interface StepContent {
  intro?: string;
  wording?: string;
  fields?: string[];
  choices?: { key?: string; text?: string }[];
  questions?: { key?: string; text?: string }[];
  documents?: { name?: string; label?: string; url?: string }[];
}

/** The household and child-details fields a step asks for, in the office's words. */
const FIELD_LABELS: Record<string, string> = {
  address: 'Address',
  guardians: 'Guardians',
  emergencyContacts: 'Emergency contacts',
  authorizedPickup: 'Authorized pickup',
  churchAffiliation: 'Church affiliation',
  dateOfBirth: 'Date of birth',
  gender: 'Gender',
  currentSchool: 'Current school',
  medicalCondition: 'Medical conditions',
  specialNeeds: 'Special needs',
  insuranceInfo: 'Insurance',
  otcMedicationPermission: 'Over-the-counter medicine',
  otcMedicationsApproved: 'Approved medicines',
};

/** A family sees their child's name where the wording says {firstName}. */
const inWords = (text: string | undefined) => text?.replace(/\{firstName\}/g, "[child's first name]");

/** What a step shows families, as far as its content says: intro, wording, fields, choices, questions, documents. */
function StepDetails({ step }: { step: EnrollmentChecklistStep }) {
  const content = (step.content ?? {}) as StepContent;
  const { intro, wording, fields = [], choices = [], questions = [], documents = [] } = content;
  if (!intro && !wording && fields.length === 0 && choices.length === 0 && questions.length === 0 && documents.length === 0) {
    return null;
  }

  return (
    <details className="mt-2 text-sm">
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Details</summary>
      <div className="mt-2 space-y-3 border-l pl-3">
        {intro && <p className="whitespace-pre-line break-words">{inWords(intro)}</p>}
        {wording && <p className="whitespace-pre-line break-words">{inWords(wording)}</p>}
        {fields.length > 0 && (
          <p className="break-words">{`Asks for: ${fields.map((field) => FIELD_LABELS[field] ?? field).join(', ')}`}</p>
        )}
        {choices.length > 0 && (
          <ul className="list-disc space-y-1 pl-5">
            {choices.map((choice, index) => (
              <li key={choice.key ?? index} className="break-words">{inWords(choice.text)}</li>
            ))}
          </ul>
        )}
        {questions.length > 0 && (
          <ul className="list-disc space-y-1 pl-5">
            {questions.map((question, index) => (
              <li key={question.key ?? index} className="break-words">{inWords(question.text)}</li>
            ))}
          </ul>
        )}
        {documents.length > 0 && (
          <ul className="space-y-1">
            {documents.map((document, index) => (
              <li key={document.url ?? index}>
                <a
                  href={document.url}
                  target="_blank"
                  rel="noreferrer"
                  className="break-words text-primary underline underline-offset-2"
                >
                  {document.name ?? document.url}
                </a>
                {document.label && <span className="ml-2 text-muted-foreground">{document.label}</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}

function ChecklistCard({ checklist, schoolName }: { checklist: EnrollmentChecklistVersion; schoolName: string }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{KIND_HEADINGS[checklist.kind] ?? checklist.name}</CardTitle>
        <CardDescription>
          {`version ${checklist.version} · ${checklist.isDefault ? 'SolaStack default' : schoolName}`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <ul className="space-y-1 text-sm">
          {/* An admission has no returning children to approve. */}
          {checklist.kind !== 'ADMISSION' && (
            <li>{`Returning children are approved automatically: ${yesNo(checklist.approveReturningAutomatically)}`}</li>
          )}
          <li>{`Families can add a child on the first step: ${yesNo(checklist.newChildrenOnFirstStep)}`}</li>
          <li>{`Families can say they're seeking financial aid: ${yesNo(checklist.aidRequests)}`}</li>
          <li>{`Enrolled when every required step is done: ${yesNo(checklist.enrollWhenComplete)}`}</li>
        </ul>

        <ol className="divide-y rounded-lg border">
          {checklist.steps.map((step) => (
            <li key={step.key} className="p-3">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-medium">{step.name}</span>
                <Badge variant={step.required ? 'default' : 'secondary'}>{step.required ? 'Required' : 'Optional'}</Badge>
                <span className="text-sm text-muted-foreground">
                  {step.scope === 'child' ? 'For each child' : 'For the family'}
                </span>
              </div>
              <StepDetails step={step} />
            </li>
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

/** The school's current checklists, read aloud. Changing one is SolaStack's job, so nothing here edits. */
export function EnrollmentChecklists({ schoolId, schoolName }: EnrollmentChecklistsProps) {
  const { data, loading, error } = useEnrollmentChecklistsQuery(schoolId);

  if (loading && !data) return <SettingsLoading />;
  if (error && !data) {
    return <p className="text-sm text-destructive">The checklists could not be loaded: {error.message}. Reload the page to try again.</p>;
  }

  const checklists = data?.enrollmentChecklists ?? [];

  return (
    <div className="space-y-6">
      {checklists.length === 0 && <p className="text-sm">No checklists are installed yet.</p>}
      {checklists.map((checklist) => (
        <ChecklistCard key={checklist.kind} checklist={checklist} schoolName={schoolName} />
      ))}
      <p className="text-sm text-muted-foreground">To change a checklist, ask SolaStack.</p>
    </div>
  );
}

export default EnrollmentChecklists;
